// Drawer d'édition TVA - ouvert depuis l'onglet TVA

import React, { useEffect } from 'react';
import { Drawer, Form, Button, Space, message } from 'antd';
import { CalculatorOutlined } from '@ant-design/icons';
import { useUpdateBusiness } from '../../../hooks/useBusinesses';
import { Business, DEFAULT_BUSINESS_CONFIGURATION } from '../../../types/business';
import { TaxConfiguration } from '../../../types/tax';
import { cleanFirestoreData } from '../../../utils/cleanFirestoreData';
import TaxConfigurationStep from './TaxConfigurationStep';

interface EditTaxModalProps {
  open: boolean;
  business: Business | null;
  onClose: () => void;
  onSuccess: () => void;
}

type TaxFormValue = TaxConfiguration & { selectedRates?: string[] };

const buildTaxFormValue = (tax?: TaxConfiguration | null): TaxFormValue => {
  if (!tax) {
    return {
      defaultRateId: 'cameroon_standard',
      selectedRates: ['cameroon_standard'],
      rates: [],
      displayMode: 'inclusive',
      showTaxOnReceipts: true,
      companyTaxNumber: '',
    };
  }

  const selectedRates =
    tax.rates?.map((r) => r.id).filter(Boolean) ||
    (tax as TaxFormValue).selectedRates ||
    ['cameroon_standard'];

  return {
    defaultRateId: tax.defaultRateId || selectedRates[0] || 'cameroon_standard',
    selectedRates,
    rates: tax.rates || [],
    displayMode: tax.displayMode || 'inclusive',
    showTaxOnReceipts: tax.showTaxOnReceipts !== undefined ? tax.showTaxOnReceipts : true,
    companyTaxNumber: tax.companyTaxNumber || '',
  };
};

const toPersistedTaxConfig = (taxConfig: TaxFormValue): TaxConfiguration => ({
  defaultRateId: taxConfig.defaultRateId,
  rates: taxConfig.rates || [],
  displayMode: taxConfig.displayMode,
  showTaxOnReceipts: taxConfig.showTaxOnReceipts,
  companyTaxNumber: taxConfig.companyTaxNumber || undefined,
});

const EditTaxModal: React.FC<EditTaxModalProps> = ({
  open,
  business,
  onClose,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const updateBusinessMutation = useUpdateBusiness();

  useEffect(() => {
    if (business && open) {
      const config = business.configuration || DEFAULT_BUSINESS_CONFIGURATION;
      form.setFieldsValue({
        taxConfiguration: buildTaxFormValue(config.tax),
      });
    }
  }, [business, open, form]);

  const handleSubmit = async () => {
    if (!business) return;
    try {
      const taxConfig = form.getFieldValue('taxConfiguration') as TaxFormValue | undefined;
      if (!taxConfig) {
        message.error('Configuration TVA requise');
        return;
      }
      if (!taxConfig.rates?.length) {
        message.error('Ajoutez au moins un taux de TVA');
        return;
      }
      const config = business.configuration || DEFAULT_BUSINESS_CONFIGURATION;
      const configuration = {
        ...config,
        tax: toPersistedTaxConfig(taxConfig),
      };
      await updateBusinessMutation.mutateAsync({
        id: business.id,
        configuration: cleanFirestoreData(configuration),
      });
      message.success('Configuration TVA mise à jour');
      onSuccess();
      onClose();
      form.resetFields();
    } catch (error: any) {
      if (error.errorFields) return;
      message.error(error.message || 'Erreur lors de la mise à jour');
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  if (!business) return null;

  return (
    <Drawer
      title={
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <CalculatorOutlined style={{ color: '#722ed1' }} />
          Modifier la configuration TVA
        </span>
      }
      open={open}
      onClose={handleCancel}
      destroyOnClose
      width={640}
      extra={
        <Space>
          <Button onClick={handleCancel}>Annuler</Button>
          <Button
            type="primary"
            loading={updateBusinessMutation.isPending}
            onClick={handleSubmit}
          >
            Enregistrer
          </Button>
        </Space>
      }
    >
      <Form form={form} layout="vertical" style={{ marginTop: 8 }} preserve={false}>
        <Form.Item name="taxConfiguration" noStyle>
          <TaxConfigurationStep />
        </Form.Item>
      </Form>
    </Drawer>
  );
};

export default EditTaxModal;
